import Head from 'next/head'
import useSWR from 'swr'
import { useRouter } from 'next/router'
import { getTranslatedField, fetcher } from '@/libs/utils'
import type { SettingsType } from '@/@types/microcms-type'

type Props = {
    title?: string
    description?: string | null
    image?: {
        url: string
        width?: number
        height?: number
    }
    settings?: SettingsType
    children?: any
}

export const PageHead = ({
    title,
    description,
    image,
    settings: propSettings,
    children
}: Props) => {
    const locale = useRouter().locale

    const { data: swrSettings, error } = useSWR<SettingsType>(
        '/api/settings',
        fetcher
    )

    const settings = propSettings || swrSettings

    const DEFAULT_TITLE = 'クリエーティブ・ディレクター・コレクティブ (つづく)'
    const DEFAULT_DESC =
        'つづく物語をつくる。つづく価値をつくる。つづく幸せをつくる。つづく未来をつくる。つづく道をつくる。'
    const DEFAULT_OGP_IMAGE = 'https://tsuzuku.tokyo/ogp.png'

    const metaTitle = title
        ? title
        : settings?.meta_title
        ? getTranslatedField({
              field: settings.meta_title,
              locale
          })
        : DEFAULT_TITLE

    const metaDesc = description
        ? description
        : settings?.meta_desc
        ? getTranslatedField({
              field: settings.meta_desc,
              locale
          })
        : DEFAULT_DESC

    const ogpImage = image
        ? `https://tsuzuku.tokyo/_next/image?url=${image.url}&w=1200&q=100`
        : settings?.ogp_img
        ? `https://tsuzuku.tokyo/_next/image?url=${settings.ogp_img?.url}&w=1200&q=100`
        : DEFAULT_OGP_IMAGE

    const imgWidth = 1200
    const imgHeight =
        image?.height && image?.width
            ? Math.floor((1200 * image?.height) / image?.width)
            : 630

    return (
        <Head>
            <title key="title">{metaTitle}</title>
            <meta
                key="viewport"
                name="viewport"
                content="width=device-width,initial-scale=1.0,maximum-scale=1.0"
            />
            <meta key="description" name="description" content={metaDesc} />
            <meta key="og:title" property="og:title" content={metaTitle} />
            <meta
                key="og:description"
                property="og:description"
                content={metaDesc}
            />
            <meta key="og:image" property="og:image" content={ogpImage} />
            <meta
                key="og:image:width"
                property="og:image:width"
                content={String(imgWidth)}
            />
            <meta
                key="og:image:height"
                property="og:image:height"
                content={String(imgHeight)}
            />
            <meta key="og:type" property="og:type" content="website" />
            <meta
                key="og:url"
                property="og:url"
                content="https://tsuzuku.tokyo/"
            />
            <meta
                key="og:site_name"
                property="og:site_name"
                content={metaTitle}
            />
            <meta
                key="twitter:card"
                name="twitter:card"
                content="summary_large_image"
            />
            <meta key="twitter:site" name="twitter:site" content="@tsuzuku" />
            <meta
                key="twitter:title"
                name="twitter:title"
                content={metaTitle}
            />
            <meta
                key="twitter:description"
                name="twitter:description"
                content={metaDesc}
            />
            <meta key="twitter:image" name="twitter:image" content={ogpImage} />
            {children}
        </Head>
    )
}
